import type { $Fetch } from 'ofetch';
import type {
  ProtocoloSetorGateway, ProtocoloAssuntoGateway,
  ListProtocoloSetoresParams, ListProtocoloAssuntosParams,
} from '@/application/port/out/ProtocoloCadastrosGateway';
import type {
  ProtocoloSetor, ProtocoloSetorCommand,
  ProtocoloAssunto, ProtocoloAssuntoCommand,
} from '@/domain/model/Protocolo';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateways de cadastros de Protocolo.
 * Authority: acadadmin:protocolo_setor:read|manage (compartilhado pra Setor+Assunto).
 *
 * BE responde Spring Page<T> em ambos os GET — gateway expõe Page<T>.
 *
 * @since G.5.5
 */
export class HttpProtocoloSetorGateway implements ProtocoloSetorGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: ListProtocoloSetoresParams = {}): Promise<Page<ProtocoloSetor>> {
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.onlyActive !== undefined) query.onlyActive = params.onlyActive;
    const res = await this.client<ApiResponse<Page<ProtocoloSetor>>>(
      '/api/v1/admin/protocolo-setores', { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<ProtocoloSetor> {
    const res = await this.client<ApiResponse<ProtocoloSetor>>(
      `/api/v1/admin/protocolo-setores/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(cmd: ProtocoloSetorCommand): Promise<ProtocoloSetor> {
    const res = await this.client<ApiResponse<ProtocoloSetor>>(
      '/api/v1/admin/protocolo-setores', { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, cmd: ProtocoloSetorCommand): Promise<ProtocoloSetor> {
    const res = await this.client<ApiResponse<ProtocoloSetor>>(
      `/api/v1/admin/protocolo-setores/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivate(id: string): Promise<void> {
    await this.client<void>(
      `/api/v1/admin/protocolo-setores/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

export class HttpProtocoloAssuntoGateway implements ProtocoloAssuntoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: ListProtocoloAssuntosParams = {}): Promise<Page<ProtocoloAssunto>> {
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.setorId) query.setorId = params.setorId;
    if (params.onlyActive !== undefined) query.onlyActive = params.onlyActive;
    const res = await this.client<ApiResponse<Page<ProtocoloAssunto>>>(
      '/api/v1/admin/protocolo-assuntos', { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<ProtocoloAssunto> {
    const res = await this.client<ApiResponse<ProtocoloAssunto>>(
      `/api/v1/admin/protocolo-assuntos/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(cmd: ProtocoloAssuntoCommand): Promise<ProtocoloAssunto> {
    const res = await this.client<ApiResponse<ProtocoloAssunto>>(
      '/api/v1/admin/protocolo-assuntos', { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, cmd: ProtocoloAssuntoCommand): Promise<ProtocoloAssunto> {
    const res = await this.client<ApiResponse<ProtocoloAssunto>>(
      `/api/v1/admin/protocolo-assuntos/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivate(id: string): Promise<void> {
    await this.client<void>(
      `/api/v1/admin/protocolo-assuntos/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
